import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export const EASE = [0.16, 1, 0.3, 1];

/** Scroll-reveal wrapper: fade-up once when 15% enters view. */
export function Reveal({ children, delay = 0, className = "", y = 28, ...rest }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** Signature on-load masked line-by-line reveal for hero headlines. */
export function KineticLines({ lines, delay = 0.15, className = "", lineClassName = "", testid }) {
  const reduced = useReducedMotion();
  return (
    <span className={`block w-full min-w-0 ${className}`} data-testid={testid}>
      {lines.map((l, i) => (
        <span key={i} className="block w-full overflow-hidden pb-[0.12em] -mb-[0.12em]" data-testid={testid ? `${testid}-line-${i + 1}` : undefined}>
          <motion.span
            className={`block w-full whitespace-normal break-normal will-change-transform ${lineClassName}`}
            initial={reduced ? false : { y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: delay + i * 0.13, ease: EASE }}
          >
            {l}{i < lines.length - 1 ? " " : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/** A readable, responsive freight-services ribbon. */
export function Marquee({ items }) {
  return (
    <div className="bg-white border-y border-slate-200/70 py-6" aria-label="Our services" data-testid="services-ribbon">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
        {items.map((item, index) => (
          <span key={item} className="flex items-center gap-3 font-display text-xs sm:text-sm font-semibold text-slate-600" data-testid={`services-ribbon-item-${index}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" aria-hidden="true" />{item}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Eyebrow({ children, light = false }) {
  return <p className={`label ${light ? "text-brand-light" : "text-corpblue"}`}>{children}</p>;
}

export function SectionHead({ eyebrow, title, lead, light = false, className = "" }) {
  return (
    <div className={`max-w-2xl ${className}`}>
      <Reveal>
        <Eyebrow light={light}>{eyebrow}</Eyebrow>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className={`mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] ${light ? "text-white" : "text-ink"}`}>
          {title}
        </h2>
      </Reveal>
      {lead && (
        <Reveal delay={0.16}>
          <p className={`mt-4 text-base lg:text-lg leading-relaxed ${light ? "text-slate-300" : "text-slate-600"}`}>{lead}</p>
        </Reveal>
      )}
    </div>
  );
}

/** Exact reference gradient shared by inner pages. */
export function PageHero({ eyebrow, title, lead, children, testid = "page-hero-title", back, overlap = false }) {
  return (
    <section className="hero-reference relative overflow-hidden text-white" data-testid="page-hero">
      <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 ${overlap ? "pb-36 lg:pb-44" : "pb-14 lg:pb-20"}`}>
        {back}
        <Reveal>
          <Eyebrow light>{eyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl leading-[1.1]" data-testid={testid}>
            {title}
          </h1>
        </Reveal>
        {lead && (
          <Reveal delay={0.16}>
            <p className="mt-5 text-base lg:text-lg text-slate-200 leading-relaxed max-w-2xl">{lead}</p>
          </Reveal>
        )}
        {children}
      </div>
    </section>
  );
}

const ARROW = "h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5";

export function PrimaryButton({ to, href, children, testid, className = "", onClick, type }) {
  const cls = `group btn-pill primary-action px-7 py-3.5 text-[15px] hover:-translate-y-0.5 hover:shadow-glow ${className}`;
  if (to)
    return (
      <Link to={to} data-testid={testid} className={cls} onClick={onClick}>
        {children}
        <ArrowUpRight className={ARROW} />
      </Link>
    );
  if (href)
    return (
      <a href={href} data-testid={testid} className={cls} onClick={onClick}>
        {children}
        <ArrowUpRight className={ARROW} />
      </a>
    );
  return (
    <button type={type || "button"} data-testid={testid} className={cls} onClick={onClick}>
      {children}
      <ArrowUpRight className={ARROW} />
    </button>
  );
}

export function GhostButton({ to, children, testid, light = false, className = "" }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group btn-pill px-7 py-3.5 text-[15px] border hover:-translate-y-0.5 ${
        light
          ? "border-white/20 text-white bg-transparent hover:bg-white/10"
          : "border-ink/15 text-ink bg-white hover:border-signal hover:text-corpblue hover:shadow-soft"
      } ${className}`}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

/** Stat used inside the hero's framed image card. */
export function StatChip({ k, v, light = false }) {
  return (
    <div className={light ? "" : "card-sm px-5 py-4"}>
      <p className={`font-display text-xl font-bold leading-none ${light ? "text-white" : "text-ink"}`}>{k}</p>
      <p className={`mt-1.5 text-[13px] leading-snug ${light ? "text-white" : "text-slate-500"}`}>{v}</p>
    </div>
  );
}

/** Accent bullet list (signal dot + text), used across panels. */
export function DotList({ items, light = false, testid }) {
  return (
    <ul className="space-y-3.5" data-testid={testid}>
      {items.map((b, i) => (
        <Reveal key={b} delay={0.05 * i}>
          <li className={`flex items-start gap-3 text-[15px] font-medium ${light ? "text-slate-200" : "text-slate-700"}`}>
            <span className="mt-[7px] h-2 w-2 rounded-full bg-gold shrink-0" />
            {b}
          </li>
        </Reveal>
      ))}
    </ul>
  );
}
